import { FC } from "react";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";

import { Enterprise as App, ChartFilters, FavouritesFilterConfig } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Col, Row, CtraLayout, Charts as ChartUI, Analytics } from "@ctra/components";
import { defaultLocale, useTranslation, Enterprise } from "@ctra/i18n";
import { ChartLegendValuesContext } from "@ctra/charts";

import { Chart, GAActions as ChartGAActions, ChartAPIContext, ChartFilterContext } from "@chart";
import { useFarm } from "@farms";
import { Routes } from "@routes";
import { useFavourites } from "@analytics";

import styles from "./Favourites.module.less";

const { ContentWrapper } = CtraLayout;
const { EmptyFavourites } = Analytics;
const { ChartVariant } = ChartUI;

/**
 * Favourite charts list
 * @constructor
 */
export const Favourites: FC = () => {
  const { farm } = useFarm();
  const { trackEvent } = useGoogleAnalytics<{ label: string }>();
  const { t } = useTranslation();
  const { favourites } = useFavourites();
  const search = new URLSearchParams();

  const {
    chart: {
      title: chartTitle,
      favourites: {
        empty: { title, description }
      }
    }
  } = Enterprise;

  if (_.isEmpty(favourites)) {
    return <EmptyFavourites className={styles.Empty} title={t(title)} description={t(description)} />;
  }

  return (
    <Row gutter={[16, 16]} className={styles.Wrapper}>
      {_.map(_.values(favourites), ({ dataDescriptorID, view, dateRange, displayFilters, dataFilters }) => {
        /**
         * dateRange can either b iso duration in an array e.g ["P1M"] or a custom range e.g. ["2023-05-01", "2023-06-01"]
         * Supply props to api context based on length of array
         */
        const isoDuration = dateRange && _.size(dateRange) === 1 ? _.first(dateRange) : null;
        const [startDate, endDate] = dateRange && _.size(dateRange) === 2 ? dateRange : [];
        const durationProps = isoDuration
          ? { isoDuration }
          : startDate && { timePeriod: { startDate, endDate }, isoDuration: null };

        const filters = [
          ...(displayFilters?.length ? displayFilters : []),
          ...(dataFilters?.length ? dataFilters : [])
        ];

        const dimGroupKey = _.find(filters, {
          type: FavouritesFilterConfig.dimGroup
        })?.values;
        const herdGroupID = _.find(filters, {
          type: FavouritesFilterConfig.herdGroup
        })?.values;
        const legendValues = _.find(displayFilters, { type: FavouritesFilterConfig.legend })?.values;

        return (
          <Sentry.ErrorBoundary fallback={<>{null}</>}>
            <ChartLegendValuesContext.Provider initialValues={legendValues as string[]}>
              <ChartFilterContext.Provider
                key={dataDescriptorID}
                filters={{ dimGroupKey, herdGroupID } as ChartFilters}
                {...durationProps}
              >
                <ChartAPIContext.Provider
                  dataDescriptorID={dataDescriptorID}
                  farmID={farm?.id}
                  key={dataDescriptorID}
                  defaultView={view}
                >
                  <ChartAPIContext.Consumer>
                    {({ chart: { title } }) => {
                      return (
                        <Col lg={12} span={24}>
                          <ContentWrapper
                            interactive
                            onClick={() => {
                              trackEvent(ChartGAActions.expandChart, {
                                label: t<string>(chartTitle(title), { lng: defaultLocale })
                              });

                              App.history.push({
                                state: {
                                  zoom: {
                                    dataDescriptorID,
                                    ...durationProps,
                                    view,
                                    filters: {
                                      dimGroupKey,
                                      herdGroupID
                                    },
                                    legendValues
                                  },
                                  timeline: {
                                    disabled: true
                                  }
                                }
                              });
                            }}
                            padded={false}
                          >
                            <ChartFilterContext.Consumer>
                              {({ isoDuration }) => (
                                <Chart
                                  variant={ChartVariant.V2}
                                  viewOptions={{ showFavouriteIcon: true, showToolbar: true }}
                                  handleCompare={() => {
                                    search.append("dataDescriptorID", dataDescriptorID);
                                    isoDuration && search.append("isoDuration", isoDuration);

                                    App.history.push({
                                      pathname: Routes.app.analytics.compareCharts.index,
                                      search: search.toString()
                                    });
                                  }}
                                />
                              )}
                            </ChartFilterContext.Consumer>
                          </ContentWrapper>
                        </Col>
                      );
                    }}
                  </ChartAPIContext.Consumer>
                </ChartAPIContext.Provider>
              </ChartFilterContext.Provider>
            </ChartLegendValuesContext.Provider>
          </Sentry.ErrorBoundary>
        );
      })}
    </Row>
  );
};
